import { getApiBaseUrl } from '../lib/api';
import { API_URL } from '../lib/config';
import React, { useState, useRef, useEffect } from 'react';
import { Send, Code, Eye, Loader2, Globe, ChevronLeft, MessageSquare, Sparkles, Bot, User, Check, FileCode2, Activity, Wand2 } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
// import StackBlitzPreview from './StackBlitzPreview';
import type { Project } from '../types';
import LivePreviewFrame from './LivePreviewFrame';

// Feature flag: controls visibility of the "Code" button
const SHOW_CODE_BUTTON = false;

  interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  files?: Record<string, string>;
  summary?: string;
    version?: number;
}

interface ProjectData {
  id: string;
  name: string;
  currentVersion: number;
  files: Record<string, string>;
}

interface InterfaceProps {
  project: Project;
  previewReviewLevel?: number;
}

export const Interface: React.FC<InterfaceProps> = ({ project, previewReviewLevel }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'boot',
      text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [currentProject, setCurrentProject] = useState<ProjectData | null>(null);
  const [showCode, setShowCode] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [confirmReset, setConfirmReset] = useState<{ show: boolean; targetVersion: number; messageId?: string }>(
    { show: false, targetVersion: 0 }
  );
  const [isChatCollapsed, setIsChatCollapsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { createInterface, iterateProject, publishProject, getProjectCode, getProjectDeploymentUrl, getChatHistory } = useProjects();
  const hasDeployment = !!getProjectDeploymentUrl(project.id);
  const loadingSteps = ['Analyzing project', 'Designing UI', 'Generating code', 'Updating preview'];
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const loadingRef = useRef(false);
  const stepPercentCaps = [15, 45, 80, 95];
  const hasInitializedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => { loadingRef.current = isLoading || isInitializing; }, [isLoading, isInitializing]);

  // Sequential loading progression with longer dwell on later steps
  useEffect(() => {
    if (!(isLoading || isInitializing)) return;
    let cancelled = false;
    setActiveStepIndex(0);

    // Target ~2 minutes total, with longer dwell on the last two steps
    const minDurations = [12000, 20000, 40000, 25000];
    const maxDurations = [20000, 30000, 60000, 45000];
    const timers: ReturnType<typeof setTimeout>[] = [];

    const advance = (i: number) => {
      if (cancelled) return;

      // If loading finished mid-step, stick on one of the last steps
      if (!loadingRef.current) {
        setActiveStepIndex(Math.max(2, i));
        return;
      }

      setActiveStepIndex(i);

      // Last step: keep pulsing here until finished
      if (i === loadingSteps.length - 1) {
        const t = setTimeout(() => {
          if (!cancelled && loadingRef.current) advance(i);
        }, 1200);
        timers.push(t);
        return;
      }

      const dMin = minDurations[i] ?? 1000;
      const dMax = maxDurations[i] ?? 2000;
      const delay = Math.round(dMin + Math.random() * (dMax - dMin));
      const t = setTimeout(() => {
        if (!cancelled) advance(i + 1);
      }, delay);
      timers.push(t);
    };

    advance(0);

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [isLoading, isInitializing]);

  useEffect(() => {
    if (currentProject && currentProject.files) {
      const fileNames = Object.keys(currentProject.files);
      if (fileNames.length > 0 && !selectedFile) {
        setSelectedFile(fileNames[0]);
      }
    }
  }, [currentProject, selectedFile]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Iterate on the provided project
      const result = await iterateProject(project.id, inputValue);

      if (result.success) {
        setCurrentProject(prev => prev ? {
          ...prev,
          currentVersion: result.version || prev.currentVersion,
          files: result.files || prev.files
        } : null);

        const aiMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: `Version ${result.version ?? (currentProject?.currentVersion ?? 1)}`,
          isUser: false,
          timestamp: new Date(),
          files: result.files,
          version: result.version
        };
        setMessages(prev => [...prev, aiMessage]);

        // Hydrate from server to ensure persistence and versions are accurate
        try {
          const chatResult = await getChatHistory(project.id);
          if (chatResult.success && chatResult.messages && chatResult.messages.length > 0) {
            const boot: Message = {
              id: 'boot',
              text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
              isUser: false,
              timestamp: new Date()
            };
            const restored = chatResult.messages.map((m) => ({
              id: m.id,
              text: m.role === 'assistant' ? (`Version ${(m as any).version ?? currentProject?.currentVersion ?? 1}`) : m.content,
              isUser: m.role === 'user',
              timestamp: new Date(m.created_at),
              version: (m as any).version
            }));
            setMessages([boot, ...restored]);
          }
        } catch {}
      } else if (result.error) {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: `Failed to update interface: ${result.error}`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePublish = async () => {
    if (!project?.id) return;
    
    setIsLoading(true);
    try {
      const result = await publishProject(project.id);
      if (result.success && result.deployment) {
        const message: Message = {
          id: Date.now().toString(),
          text: `🎉 Your project has been published! View it here: ${result.deployment.url}`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, message]);
      } else if (result.error) {
        const errorMessage: Message = {
          id: Date.now().toString(),
          text: `Failed to publish: ${result.error}`,
          isUser: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: `Failed to publish: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Bootstrap: load existing interface or create initial one from project spec
  useEffect(() => {
    // Skip if already initialized for this project
    if (hasInitializedRef.current && currentProject?.id === project.id) {
      return;
    }
    
    let isCancelled = false;
    let chatHydrated = false;
    const pollForCode = async (attempts = 300, delayMs = 1000) => {
      for (let i = 0; i < attempts; i++) {
        if (isCancelled) break;
        if (i % 10 === 0 && i > 0) {
          console.log(`[Interface] Still waiting for code generation... (${i} seconds)`);
        }
        const result = await getProjectCode(project.id);
        if (result.success && result.files) {
          if (isCancelled) break;
          setCurrentProject({
            id: project.id,
            name: project.name,
            currentVersion: result.version || 1,
            files: result.files
          });
          setIsInitializing(false);
          hasInitializedRef.current = true;

          // Attempt a second chat hydrate if first attempt failed or had no messages
          if (!chatHydrated) {
            try {
              const chatResult = await getChatHistory(project.id);
              if (chatResult.success && chatResult.messages && chatResult.messages.length > 0) {
                const boot: Message = {
                  id: 'boot',
                  text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
                  isUser: false,
                  timestamp: new Date()
                };
                const restored = chatResult.messages.map((m) => ({
                  id: m.id,
                  text: m.content,
                  isUser: m.role === 'user',
                  timestamp: new Date(m.created_at),
                  version: (m as any).version
                }));
                setMessages([boot, ...restored]);
                chatHydrated = true;
              }
            } catch {}
          }
          return;
        }
        await new Promise(r => setTimeout(r, delayMs));
      }
      if (!isCancelled) setIsInitializing(false);
    };

    const initialize = async () => {
      if (!project?.id) return;
      try {
        // Optimistically mark as owner to enable generation path if role is missing
        // (UI gating is done in parent but this ensures minimal friction)
        // Load existing chat history first
        try {
          const chatResult = await getChatHistory(project.id);
          if (chatResult.success && chatResult.messages) {
            const boot: Message[] = [
              {
                id: 'boot',
                text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
                isUser: false,
                timestamp: new Date(),
                version: undefined
              }
            ];
            const restored = chatResult.messages.map((m) => ({
              id: m.id,
              text: m.role === 'assistant' ? (`Version ${(m as any).version ?? currentProject?.currentVersion ?? 1}`) : m.content,
              isUser: m.role === 'user',
              timestamp: new Date(m.created_at),
              version: (m as any).version
            }));
            const all = [...boot, ...restored];
            setMessages(all);
            if (restored.length > 0) chatHydrated = true;
          }
        } catch {}

        // Try to fetch latest code
        const codeResult = await getProjectCode(project.id);
        if (codeResult.success && codeResult.files) {
          if (isCancelled) return;
          setCurrentProject({
            id: project.id,
            name: project.name,
            currentVersion: codeResult.version || 1,
            files: codeResult.files
          });
          hasInitializedRef.current = true;
          // Try hydrating chat again if not yet done
          if (!chatHydrated) {
            try {
              const chatResult = await getChatHistory(project.id);
              if (chatResult.success && chatResult.messages && chatResult.messages.length > 0) {
                const boot: Message = {
                  id: 'boot',
                  text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
                  isUser: false,
                  timestamp: new Date()
                };
            const restored = chatResult.messages.map((m) => ({
                  id: m.id,
              text: m.role === 'assistant' ? (`Version ${(m as any).version ?? currentProject?.currentVersion ?? 1}`) : m.content,
                  isUser: m.role === 'user',
                  timestamp: new Date(m.created_at),
                  version: (m as any).version
                }));
                setMessages([boot, ...restored]);
                chatHydrated = true;
              }
            } catch {}
          }
          return;
        }

        // No code yet; show initializing state and create/poll
        setIsInitializing(true);
        // If no versions exist yet, create the initial interface (no template)
        const createResponse = await createInterface(project.id);
        if (isCancelled) return;
        if (createResponse.success && createResponse.files) {
          setCurrentProject({
            id: project.id,
            name: project.name,
            currentVersion: 1,
            files: createResponse.files
          });
          setIsInitializing(false);
          hasInitializedRef.current = true;
          // Final attempt to hydrate chat
          if (!chatHydrated) {
            try {
              const chatResult = await getChatHistory(project.id);
              if (chatResult.success && chatResult.messages && chatResult.messages.length > 0) {
                const boot: Message = {
                  id: 'boot',
                  text: "Hello! I've generated an interface based on your project details. If you'd like to change anything, describe it here and I'll update the interface.",
                  isUser: false,
                  timestamp: new Date()
                };
                const restored = chatResult.messages.map((m) => ({
                  id: m.id,
                  text: m.role === 'assistant' ? (`Version ${(m as any).version ?? 1}`) : m.content,
                  isUser: m.role === 'user',
                  timestamp: new Date(m.created_at),
                  version: (m as any).version
                }));
                setMessages([boot, ...restored]);
                chatHydrated = true;
              }
            } catch {}
          }
          return;
        }

        // If creation did not return files (pending or already exists), poll until ready (longer window)
        await pollForCode(60, 2000);
      } catch (error) {
        if (!isCancelled) {
          setIsInitializing(false);
          const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: `Error preparing interface: ${error instanceof Error ? error.message : 'Unknown error'}`,
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      }
    };
    initialize();
    return () => { 
      isCancelled = true;
      // Reset initialization flag when component unmounts or project changes
      if (currentProject?.id !== project?.id) {
        hasInitializedRef.current = false;
      }
    };
  }, [project?.id]);

  return (
    <div className="h-full flex">
      {/* Left Panel - Chat Interface */}
      {!isChatCollapsed && (
        <div className="w-[28rem] flex flex-col border-r border-gray-200">
          {/* Chat Header */}
          <div className="px-3 py-2 border-b border-gray-200 bg-white/80 supports-[backdrop-filter]:bg-white/60 backdrop-blur sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900 leading-tight">AI Interface Builder</h3>
                <p className="text-xs text-gray-600">
                  {project ? `Working on: ${project.name}` : 'Describe what you want to build'}
                </p>
              </div>
              <button
                className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                onClick={() => setIsChatCollapsed(true)}
                aria-label="Collapse chat"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span className="text-xs">Hide</span>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
          {messages.map((message) => (
            <div key={message.id} className={`flex items-end gap-2 ${message.isUser ? 'justify-end' : 'justify-start'}`}>
              {!message.isUser && (
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center text-gray-700">
                  <Bot className="w-4 h-4" />
                </div>
              )}
              <div
                className={`max-w-[75%] px-3 py-2.5 rounded-2xl shadow-sm ${
                  message.isUser ? '' : 'bg-white text-gray-900 border border-gray-200'
                }`}
                style={message.isUser ? { backgroundColor: 'color-mix(in oklab, var(--accent) 10%, white)' } : undefined}
              >
                {message.isUser ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                ) : message.id === 'boot' ? (
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500 mt-0.5" />
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.text}</p>
                  </div>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    Version {message.version ?? currentProject?.currentVersion ?? 1}
                  </span>
                )}
                {!message.isUser && ((message.version ?? currentProject?.currentVersion ?? 1) !== (currentProject?.currentVersion ?? 1)) && (
                  <div className="mt-2 flex items-center justify-start text-xs">
                    <button
                      className="font-medium"
                      style={{ color: 'var(--accent)' }}
                      onClick={() => {
                        const targetVersion = message.version ?? currentProject?.currentVersion ?? 1;
                        setConfirmReset({ show: true, targetVersion, messageId: message.id });
                      }}
                    >
                      Reset to this version
                    </button>
                  </div>
                )}
                <p className={`text-[10px] mt-1 ${message.isUser ? 'text-zinc-500' : 'text-gray-500'}`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              {message.isUser && (
                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-gray-700" style={{ backgroundColor: 'color-mix(in oklab, var(--accent) 25%, white)' }}>
                  <User className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white text-gray-900 border border-gray-200 px-4 py-3 rounded-2xl shadow-sm w-full max-w-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-4 h-4 text-gray-500 animate-pulse" />
                  <span className="text-sm text-gray-600">Generating interface…</span>
                  <span className="text-xs text-gray-500 ml-auto">{stepPercentCaps[activeStepIndex]}%</span>
                </div>
                <div className="flex flex-col gap-1.5">
                  {loadingSteps.map((label, idx) => (
                    <div key={label} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${idx === activeStepIndex ? 'bg-[var(--accent)] animate-pulse' : 'bg-gray-300'}`} />
                      <span className={`text-xs ${idx === activeStepIndex ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>{label}</span>
                      {idx < activeStepIndex && <Check className="w-3 h-3 text-green-600 ml-auto" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t border-gray-200 bg-white/80 supports-[backdrop-filter]:bg-white/60 backdrop-blur">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentProject ? "Describe changes you want to make..." : (isInitializing ? "Generating interface..." : "Preparing interface for this project...")}
                  className="w-full px-4 py-3 pr-12 border border-zinc-200 rounded-2xl focus:border-transparent resize-none shadow-sm bg-white/90"
                  style={{ outlineColor: 'var(--focus-ring)' }}
                  rows={2}
                  disabled={isLoading || isInitializing}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading || !currentProject || isInitializing}
                  className="absolute right-2 bottom-2 rounded-full w-9 h-9 flex items-center justify-center text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--accent)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right Panel - Preview and Code */}
      <div className="flex-1 flex flex-col">
        {/* Container Header */}
        <div className="px-3 py-2 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 leading-tight">
                  {showCode ? 'Code Editor' : 'Live Preview'}
                </h3>
                <p className="text-xs text-gray-600">
                  {currentProject ? `Version ${currentProject.currentVersion}` : 'Your interface will appear here'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isChatCollapsed && (
                <button
                  onClick={() => setIsChatCollapsed(false)}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  aria-expanded={!isChatCollapsed}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span className="text-xs">Show Chat</span>
                </button>
              )}
              {SHOW_CODE_BUTTON && (
                <button
                  onClick={() => setShowCode(!showCode)}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  {showCode ? <Eye className="w-3.5 h-3.5" /> : <Code className="w-3.5 h-3.5" />}
                  <span className="text-xs">{showCode ? 'Preview' : 'Code'}</span>
                </button>
              )}
              {currentProject && (
                <button
                  onClick={() => {
                    alert(`Publishing project: ${project?.id}`);
                    handlePublish();
                  }}
                  disabled={isLoading}
                  className="flex items-center gap-1.5 px-3 py-1 text-white rounded-full transition-colors disabled:bg-gray-300 text-sm"
                  style={{ backgroundColor: 'var(--accent)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span className="text-xs">{hasDeployment ? 'Update' : 'Publish'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 bg-white overflow-hidden">
          {showCode && currentProject ? (
            <div className="h-full flex">
              {/* File List */}
              <div className="w-48 border-r border-gray-200 bg-gray-50 p-2">
                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-2">
                  Files
                </h4>
                <div className="space-y-1">
                  {Object.keys(currentProject.files).map((filename) => (
                    <button
                      key={filename}
                      onClick={() => setSelectedFile(filename)}
                      className={`w-full text-left px-2 py-1 text-sm rounded transition-colors ${
                        selectedFile === filename
                          ? 'bg-amber-50 text-amber-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {filename}
                    </button>
                  ))}
                </div>
              </div>

              {/* Code Editor */}
              <div className="flex-1 p-4">
                <pre className="h-full overflow-auto bg-gray-900 text-gray-100 p-4 rounded-lg text-sm">
                  <code>{currentProject.files[selectedFile] || ''}</code>
                </pre>
              </div>
            </div>
          ) : (
            isInitializing || !currentProject || !currentProject.files || Object.keys(currentProject.files).length === 0 ? (
              <div className="h-full w-full flex items-center justify-center">
                <div className="w-full max-w-xl p-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <Wand2 className="w-5 h-5 text-gray-600" />
                    <span className="text-gray-800 font-medium">Preparing your live preview…</span>
                    <span className="ml-auto text-sm text-gray-500">{Math.round(((activeStepIndex + 1) / loadingSteps.length) * 100)}%</span>
                  </div>
                  <div className="space-y-3">
                    {loadingSteps.map((label, idx) => (
                      <div key={label} className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${idx <= activeStepIndex ? 'border-[var(--accent)] text-[var(--accent)]' : 'border-gray-300 text-gray-300'}`}>
                          {idx < activeStepIndex ? <Check className="w-3 h-3" /> : <Loader2 className={`w-3 h-3 ${idx === activeStepIndex ? 'animate-spin' : ''}`} />}
                        </div>
                        <span className={`text-sm ${idx === activeStepIndex ? 'text-gray-900' : 'text-gray-500'}`}>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-3" aria-hidden>
                    <div className="h-14 rounded-lg bg-gray-100 animate-pulse" />
                    <div className="h-14 rounded-lg bg-gray-100 animate-pulse" />
                    <div className="h-14 rounded-lg bg-gray-100 animate-pulse" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full w-full">
                {/* Live preview component */}
                {currentProject && (
                  <LivePreviewFrame
                    className="h-full w-full"
                    files={currentProject.files}
                    supabaseUrl={(import.meta as any).env.VITE_SUPABASE_URL || ''}
                    supabaseAnonKey={(import.meta as any).env.VITE_SUPABASE_ANON_KEY || ''}
                    previewMode={true}
                    projectId={currentProject.id}
                    apiUrl={API_URL}
                    previewReviewLevel={previewReviewLevel}
                  />
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Confirm Reset Modal */}
      {confirmReset.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmReset({ show: false, targetVersion: 0 })} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-5">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Reset to version {confirmReset.targetVersion}?</h4>
            <p className="text-sm text-gray-600 mb-4">
              This will delete all chat messages after the selected assistant message and load the code for version {confirmReset.targetVersion}. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setConfirmReset({ show: false, targetVersion: 0 })}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={async () => {
                  const targetVersion = confirmReset.targetVersion;
                  const messageId = confirmReset.messageId;
                  setConfirmReset({ show: false, targetVersion: 0 });
                  // Trim messages in UI
                  setMessages((prev) => {
                    const idx = messageId ? prev.findIndex((m) => m.id === messageId) : -1;
                    return idx === -1 ? prev : prev.slice(0, idx + 1);
                  });
                  // Update version and fetch files for that version
                  setCurrentProject((prev) => (prev ? { ...prev, currentVersion: targetVersion } : prev));
                  try {
                    const { getAuthToken } = await import('../lib/supabase');
                    const token = await getAuthToken();
                    if (token) {
                     const API_URL = getApiBaseUrl();
                      // Persist trim on server
                      try {
                        await fetch(`${API_URL}/api/projects/${project.id}/chat/trim`, {
                          method: 'POST',
                          headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ beforeMessageId: messageId, targetVersion })
                        });
                      } catch {}
                      // Load code for the chosen version
                      const resp = await fetch(`${API_URL}/api/projects/${project.id}/code/${targetVersion}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (resp.ok) {
                        const data = await resp.json();
                        if (data && data.files) {
                          setCurrentProject((prev) => prev ? { ...prev, currentVersion: targetVersion, files: data.files } : prev);
                        }
                      }
                    }
                  } catch {}
                }}
              >
                Yes, reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};